import { isEmpty } from 'lodash-es';

import {
  useFetchPostCommentReplies,
  useUpdateCommentReaction,
} from '@/actions/post-comment.actions';
import { ProfileTypes } from '@/constants/profileType.constant';
import { cn } from '@/lib/utils';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import Link from '@ui/Link';
import Typography from '@ui/Typography';
import { Separator } from '@ui/separator';
import { LucideLoader } from 'lucide-react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import React from 'react';
import AppLink from '../next-ui/Link/Link';
import ImageViewDialog from './ImageViewDialog';
import PostCommentForm from './PostCommentForm';
import PostCommentMenu from './PostCommentMenu';
import PostMessage from './PostMessage';
import dynamic from 'next/dynamic';
const ReactionButton = dynamic(() => import('./ReactionButton'), {
  ssr: false,
});

import Reactions from './Reactions';
import TimeSincePost from './TimeSincePost';

const AuthorChip = () => {
  return (
    <div className="ml-2 rounded-md bg-text-secondary px-1 py-0.5">
      <Typography variant="caption" color="white" className="font-medium">
        Author
      </Typography>
    </div>
  );
};

export default function PostComment(props) {
  const { post, comment, onStateChange = () => {}, disabled = false } = props;
  const [status, setStatus] = React.useState(null);
  const [replyAnimation, setReplyAnimation] = React.useState({ blink: false });
  const [editing, setEditing] = React.useState(false);
  const [showImageInFull, setShowImageInFull] = React.useState(false);

  const isChildComment = !isEmpty(comment.parentCommentId);
  const isAuthor =
    post.author.id === comment.authorId && post.postAs === comment.postAs;

  const {
    data,
    isLoading: fetchingReplies,
    hasNextPage,
    fetchNextPage,
  } = useFetchPostCommentReplies(post, comment);

  const { mutate: updateReaction } = useUpdateCommentReaction();

  // derived states

  // const isNonProfitAdmin = useStore((state) => state.user.isNonProfitAdmin);
  // const hasReplies =
  //   useStore((state) => state.socialFeed.hasMoreRepliesByComment[comment.id]) ||
  //   false;

  // const isEditing =
  //   useStore((state) => state.socialFeed.editingCommentId) === comment.id;
  // const nonProfit =
  //   useStore((state) => state.nonProfit.cached[comment.author.nonProfitId]) ||
  //   null;

  // const jobTitle = isNonProfitAdmin
  //   ? `${comment.author.designation} - ${comment.author.orgDetails.name}`
  //   : comment.author.designation;
  // const replyHelperText = '';

  const getAttachmentUrl = () => {
    if (comment.attachmentUrl) {
      if (comment.attachmentUrl.startsWith('data:')) {
        return comment.attachmentUrl;
      }
      return getQualifiedImageUrl(comment.attachmentUrl);
    }
    return null;
  };

  const handleReply = () => {
    if (status === 'reply') {
      setReplyAnimation({ blink: true });
    }

    if (isChildComment) {
      onStateChange('reply', comment.parentCommentId);
    } else {
      setStatus('reply');
    }
  };

  const handleChildStateChange = (type) => {
    if (type === 'reply') {
      if (status === 'reply') {
        setReplyAnimation({ blink: true });
      }
      setStatus('reply');
    }
  };

  const handleLoadReplies = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  // const showPostContributorDialog = useStore(
  //   (state) => state.postContributorDialog.showDialog
  // );

  const showReactions = () => {
    // showPostContributorDialog(
    //   { id: comment.postId },
    //   null,
    //   'reactions',
    //   comment
    // );
  };

  const handleReaction = (reaction) => {
    updateReaction({ comment, reaction });
  };

  const getAuthorSlug = (comment) => {
    if (
      comment.postAs === ProfileTypes.Organization &&
      comment.author.orgDetails?.slug
    ) {
      return `/${comment.author.orgDetails.slug}`;
    } else if (
      comment.postAs === ProfileTypes.Individual &&
      comment.author.slug
    ) {
      return `/${comment.author.slug}`;
    }
    return null;
  };

  const renderProfileAvatar = () => {
    switch (comment.postAs) {
      case 'org':
        return (
          <Avatar
            src={comment.author.orgDetails.avatar}
            alt={comment.author.orgDetails.name}
            profile="npo"
            size={isChildComment ? 'sm' : 'sm'}></Avatar>
        );
      case 'me':
        return (
          <Avatar
            src={comment.author.avatar}
            alt={comment.author.fullName}
            profile="user"
            size={isChildComment ? 'sm' : 'sm'}></Avatar>
        );
      default:
        return null;
    }
  };

  const renderProfileDetails = () => {
    switch (comment.postAs) {
      case 'org':
        return (
          <>
            <div className="flex flex-col gap-1 md:flex-row md:gap-0">
              {/* // TODO: check */}
              <Link to={`/nonprofit/${comment.author.nonProfitId}`}>
                <Typography variant="h5" className="hover:text-text-hyperlink">
                  {comment.author.orgDetails.name}
                </Typography>
              </Link>
              {isAuthor && <AuthorChip />}
            </div>
            {/* {nonProfit && (
              <Typography variant="caption">
                {`${nonProfit.numFollowers} Followers`}
              </Typography>
            )} */}
          </>
        );
      case 'me':
        return (
          <div className="flex flex-col">
            <div className="flex flex-col gap-1 md:flex-row md:gap-0">
              <AppLink href={getAuthorSlug(comment)}>
                <Typography variant="h5" className="hover:text-text-hyperlink">
                  {comment.author.fullName}
                </Typography>
              </AppLink>
              {isAuthor && <AuthorChip />}
            </div>
            <Typography variant="caption" color="textDisabled">
              {comment.author.designation}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex w-full gap-3">
      <div className="pt-1">{renderProfileAvatar()}</div>

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-col">
          <div className="flex flex-col gap-1 rounded-md bg-body-background p-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start">
                {renderProfileDetails()}
                <div className="md:hidden">
                  <TimeSincePost
                    variant="caption"
                    createdOn={comment.createdOn}
                  />
                </div>
              </div>
              <div className="flex items-start gap-1">
                <div className="mr-2 hidden md:block">
                  <TimeSincePost
                    variant="caption"
                    createdOn={comment.createdOn}
                  />
                </div>

                <PostCommentMenu
                  comment={comment}
                  onEdit={() => setEditing(true)}
                />
              </div>
            </div>
            {status === 'loading' ? (
              <div className="ml-2">
                <LucideLoader className="h-4 w-4 animate-spin text-text-disabled" />
              </div>
            ) : (
              <>
                {editing && (
                  <PostCommentForm
                    comment={comment}
                    postId={comment.postId}
                    mode="edit"
                    onComplete={() => setEditing(false)}
                  />
                )}

                {!editing && (
                  <>
                    {<PostMessage message={comment.message} />}

                    {comment.attachmentUrl && (
                      <>
                        <div className="mt-2 grid w-full grid-cols-1 pl-2 sm:grid-cols-2 md:grid-cols-3">
                          <div
                            className="relative h-32 w-full"
                            role="button"
                            onClick={() => setShowImageInFull(true)}>
                            <Image
                              fill
                              sizes="(max-width: 640px) 100vw, 640px"
                              src={getAttachmentUrl() || ''}
                              alt="User uploaded image"
                              className="rounded-md object-cover object-center"
                            />
                          </div>
                        </div>
                        <ImageViewDialog
                          isOpen={showImageInFull}
                          imageUrl={getAttachmentUrl()}
                          onClose={() => setShowImageInFull(false)}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-3 py-1">
            {!disabled && (
              <ReactionButton
                userReaction={comment.userReaction}
                onChange={handleReaction}
                variant="label"
              />
            )}
            {comment.totalReactions > 0 && (
              <div className="group-hover">
                <Reactions
                  reactions={comment.reactions}
                  onClick={showReactions}>
                  <Typography variant="caption" ml={0.5}>
                    {comment.totalReactions}
                  </Typography>
                </Reactions>
              </div>
            )}
            {!disabled && (
              <>
                <Separator orientation="vertical" />
                <Button variant="plain" size="xs" onClick={handleReply}>
                  <Typography variant="caption" color="textSecondary">
                    Reply
                  </Typography>
                </Button>
              </>
            )}
            {comment.totalReplies > 0 && (
              <Typography
                variant="caption"
                color="textDisabled"
                className="py-0.5">
                {comment.totalReplies} Replies
              </Typography>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-4">
          {!isChildComment && hasNextPage && (
            <div className="flex items-center gap-2">
              <button onClick={handleLoadReplies} disabled={fetchingReplies}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={cn(fetchingReplies ? '' : 'hover:text-primary')}>
                  See older replies
                </Typography>
              </button>

              {fetchingReplies && (
                <LucideLoader className="h-4 w-4 animate-spin text-text-disabled" />
              )}
            </div>
          )}

          {data?.pages.map((group, i) => (
            <React.Fragment key={i}>
              {group.comments.map((reply) => (
                <PostComment
                  key={reply.id}
                  post={post}
                  comment={reply}
                  disabled={disabled}
                  onStateChange={handleChildStateChange}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
        {status === 'reply' && (
          <div className="my-1">
            <PostCommentForm
              animation={replyAnimation}
              postId={comment.postId}
              parentCommentId={comment.id}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const commentShape = {
  id: PropTypes.string.isRequired,
  postId: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  createdOn: PropTypes.string.isRequired,
  author: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fullName: PropTypes.string.isRequired,
    avatar: PropTypes.string,
  }).isRequired,
  parentCommentId: PropTypes.string,
  totalReplies: PropTypes.number.isRequired,
};
commentShape.replies = PropTypes.arrayOf(PropTypes.shape(commentShape));

PostComment.propTypes = {
  comment: PropTypes.shape(commentShape).isRequired,
  onStateChange: PropTypes.func,
};
